import { AssistanceStep } from '@sec-loans-ui/common/steps/assistance-step/AssistanceStep'
import { LoanCatalogStep } from '@sec-loans-ui/common/steps/loan-catalog-step/LoanCatalogStep'
import { AuthenticatedContext } from '@sec-loans-ui/common/types'
import { Wizard as IfpLoansWizard } from '@sec-loans-ui/ifp-loans/wizard/Wizard'
import { Wizard as LoanOfferWizard } from '@sec-loans-ui/loan-offer/wizard/Wizard'
import { ApplicationWizardProvider } from '@sec-loans-ui/state/applicationWizardContext'
import {
  getAndStoreInputParams,
  getAuthenticationMode,
  getSecLoansFlow,
  getSecLoansSourceChannel,
} from '@sec-loans-ui/utils/helpers'
import { Wizard as SecLoansWizard } from '@sec-loans-ui/wizard/Wizard'
import { assertValue } from '@shared/helpers'
import {
  ENDPOINT_SEC_LOANS_GET_USER_INFO,
  ENDPOINT_SEC_LOANS_VERIFY_AUTH_CODE,
  SecLoansSource,
} from '@shared/sec-loans'
import { AppId, AppName } from '@shared/types'
import { CommonWizardWrapper } from '@ui-common/components/CommonWizardWrapper'
import { AuthenticateStep } from '@ui-common/steps/AuthenticateStep'
import { AuthenticationCancelled } from '@ui-common/steps/AuthenticateStep/types'
import { GetUserInfoStep } from '@ui-common/steps/GetUserInfoStep/GetUserInfoStep'
import { InitializeStep } from '@ui-common/steps/InitializeStep'
import { getSourceChannel } from '@ui-common/utils/helpers'
import { callEndpoint } from '@ui-common/utils/http/client'
import { getLanguage } from '@ui-common/utils/language'

import { EntranceStep } from '../steps/entrance-step/EntranceStep'
import { GetAllApplicationsStep } from '../steps/get-all-applications-step/GetAllApplicationsStep'
import { WizardStep } from './rootWizardMachine'
import { useWizardLogic } from './useWizardLogic'

export const RootWizard = ({ authenticatedContext }: { authenticatedContext?: AuthenticatedContext }) => {
  const language = getLanguage()
  const {
    actor,
    analyticsService,
    send,
    authToken,
    user,
    applications,
    step,
    secLoansEntryContext,
    ifpLoansEntryContext,
    loanOfferEntryContext,
    showAbortPromptModal,
    refetchAndGoToCatalog,
    onNewApplicationSelect,
    onApplicationSelect,
    onCreatedSecLoansApplication,
    onCreatedIfpLoansApplication,
    onAutomaticOfferResponse,
    onAbort,
  } = useWizardLogic(authenticatedContext)

  const stepComponentMap: Partial<Record<WizardStep, () => React.ReactNode>> = {
    [WizardStep.Initialize]: () => (
      <InitializeStep
        appName={AppName.SecLoans}
        shouldGetConfig={true}
        actions={{
          getAndStoreInputParams,
          onInitialized: (data) => send({ type: 'NEXT_STEP', data }),
          onInitializedExit: onAbort,
        }}
      />
    ),
    [WizardStep.Entrance]: () => (
      <EntranceStep
        showProcessingTimeNotification={secLoansEntryContext?.params?.SHOW_PROCESSING_TIME_NOTIFICATION === 'true'}
        onNext={() => send({ type: 'NEXT_STEP', data: undefined })}
        onAbort={onAbort}
      />
    ),
    [WizardStep.Authenticate]: () => (
      <AuthenticateStep
        verifyAuthCodeCallback={(authCode) => {
          const sourceChannel = getSourceChannel<SecLoansSource>()
          return callEndpoint(ENDPOINT_SEC_LOANS_VERIFY_AUTH_CODE, {
            requestPayload: {
              authCode,
              uiLanguage: language,
              sourceChannel,
              flow: getSecLoansFlow(true),
            },
          })
        }}
        authMode={getAuthenticationMode(getSecLoansSourceChannel())}
        onExit={onAbort}
        onAuthenticated={(token) => send({ type: 'AUTHENTICATION_DONE', data: { authToken: token } })}
        onAuthenticationCancelled={() => send({ type: AuthenticationCancelled })}
      />
    ),
    [WizardStep.GetUserInfo]: () => (
      <GetUserInfoStep
        onAbort={showAbortPromptModal}
        getUserInfo={() => callEndpoint(ENDPOINT_SEC_LOANS_GET_USER_INFO)}
        onGetUserInfoDone={(data) => send({ type: 'GET_DATA_DONE', data })}
      />
    ),
    [WizardStep.Assistance]: () => (
      <AssistanceStep
        showProcessingTimeNotification={secLoansEntryContext?.params?.SHOW_PROCESSING_TIME_NOTIFICATION === 'true'}
        onCreatedSecLoansApplication={onCreatedSecLoansApplication}
        onCreatedIfpLoansApplication={onCreatedIfpLoansApplication}
        onGoToApplicationsClick={refetchAndGoToCatalog}
        onAbort={showAbortPromptModal}
        user={assertValue(user, 'user')}
      />
    ),
    [WizardStep.GetAllApplications]: () => (
      <GetAllApplicationsStep onAbort={showAbortPromptModal} parentService={actor} />
    ),
    [WizardStep.LoanCatalog]: () => (
      <LoanCatalogStep
        onApplicationSelect={onApplicationSelect}
        onNewApplicationSelect={onNewApplicationSelect}
        onAbort={showAbortPromptModal}
        user={assertValue(user, 'user')}
        applications={assertValue(applications, 'applications')}
      />
    ),
  }

  switch (step) {
    case WizardStep.GoToLoanOffer:
      return (
        <LoanOfferWizard
          initialContext={{
            ...loanOfferEntryContext,
            user: assertValue(user, 'user'),
            loanOfferId: assertValue(loanOfferEntryContext?.loanOfferId, `loanOfferEntryContext.loanOfferId`),
          }}
          onGoToApplicationsClick={refetchAndGoToCatalog}
          onNewApplicationClick={onNewApplicationSelect}
        />
      )
    case WizardStep.GoToIfpLoans:
      return (
        <IfpLoansWizard
          initialContext={assertValue(ifpLoansEntryContext, 'ifpLoansEntryContext')}
          onGoToApplicationsClick={refetchAndGoToCatalog}
          onNewApplicationClick={onNewApplicationSelect}
          onCancelledApplication={refetchAndGoToCatalog}
          onReturnedApplication={refetchAndGoToCatalog}
        />
      )
    case WizardStep.GoToSecLoans:
      return (
        <ApplicationWizardProvider
          onGoToCatalog={refetchAndGoToCatalog}
          initialContext={secLoansEntryContext}
          onNewApplicationClick={onNewApplicationSelect}
          onAutomaticOfferResponse={onAutomaticOfferResponse}
        >
          <SecLoansWizard />
        </ApplicationWizardProvider>
      )
    default:
      return (
        <CommonWizardWrapper
          stepComponentMap={stepComponentMap}
          step={step}
          authToken={authToken ?? null}
          appId={AppId.SecLoans}
          analyticsService={analyticsService}
          onLogout={onAbort}
          userContext={null}
        />
      )
  }
}
